/**
 * One document, whoever it is going to.
 *
 * There were two editors. The bid studio wrote a government response out of
 * columns on `bids`; the proposal builder wrote a client proposal out of
 * sections on `proposals.content`. They produced the same document — the
 * Viking proposal and a bid response have the same spine, cover through
 * acceptance — and they shared nothing, so a fix to one never reached the
 * other and neither could show you the result.
 *
 * So the spine lives here, once. A section is `{ id, type, data }`; a document
 * is a header and an ordered list of them. What differs between a bid and a
 * proposal is which sections are present, not how any of them is written or
 * drawn.
 *
 * The records stay where they are. A bid's lifecycle is a procurement portal
 * and a confirmation number; a proposal's is a link, a signature and a payment
 * plan. Those are genuinely different and they keep their own tables. It is
 * the document that is one.
 */

import { escapeHtml } from './proposalDocument'
import { quote } from './priceList'
import { lookPages } from './proposalPages/render'
import { modelVars } from './proposalPages/model'
import { unresolvedVars } from './proposalVars'

const text = (v) => String(v ?? '').trim()
const esc = escapeHtml

export const money = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n) || 0)

/* ── The vocabulary ───────────────────────────────────────────────────────
 *
 * `required` means a document of that kind cannot go out without it. Anything
 * else is offered and left out when empty — a page headed "Proof" with nothing
 * under it reads as an oversight to the one person whose job is to find them.
 */

export const SECTION_TYPES = [
  { type: 'cover', label: 'Cover', blurb: 'Who it is for, what it answers, when it closes.', fixed: true },
  { type: 'overview', label: 'Overview', blurb: 'The opening. Why them, and why us.' },
  { type: 'scope', label: 'Scope of work', blurb: 'What is delivered, item by item.' },
  { type: 'investment', label: 'Investment', blurb: 'What it costs, line by line.' },
  { type: 'proof', label: 'Proof', blurb: 'Figures that can be evidenced, and real quotes.' },
  { type: 'about', label: 'About the studio', blurb: 'Written once, the same on every document.' },
  { type: 'terms', label: 'Terms', blurb: 'The agreement, numbered.' },
  { type: 'acceptance', label: 'Acceptance', blurb: 'What they pick, and where they sign.' },
  // From the page library: a case study, a service page, the portfolio. Any
  // number of them, so it is never offered as a single "+ Section".
  { type: 'page', label: 'Library page', blurb: 'A page from the library, filled for this client. Edit it here; the library is not changed.', repeatable: true },
]

const TYPE_BY_ID = Object.fromEntries(SECTION_TYPES.map(t => [t.type, t]))

export const sectionLabel = (s) => TYPE_BY_ID[s?.type]?.label || 'Section'

/** Which sections each kind of document opens with, in order. */
export const SPINE = {
  bid: ['cover', 'overview', 'scope', 'investment', 'about'],
  proposal: ['cover', 'overview', 'scope', 'investment', 'proof', 'terms', 'acceptance'],
}

export const emptySection = (type) => ({
  id: `${type}-${Math.random().toString(36).slice(2, 9)}`,
  type,
  data: {
    cover: {},
    overview: { body: '' },
    scope: { items: [] },
    investment: { rows: [], note: '' },
    proof: { stats: [], quotes: [] },
    about: { body: '' },
    terms: { items: [] },
    acceptance: { body: '', options: [] },
    page: { title: '', body: '' },
  }[type] || {},
})

export const startingSections = (kind) => (SPINE[kind] || SPINE.bid).map(emptySection)

/* ── What is in it, and whether it can go ─────────────────────────────────── */

const hasContent = {
  cover: (d, doc) => Boolean(text(doc.title) && text(doc.recipient)),
  overview: (d) => text(d.body).length > 0,
  scope: (d) => realItems(d.items).length > 0,
  investment: (d) => investmentTotal(d) > 0,
  proof: (d) => (d.stats || []).length > 0 || (d.quotes || []).length > 0,
  about: (d) => text(d.body).length > 0,
  terms: (d) => (d.items || []).length > 0,
  acceptance: (d) => (d.options || []).length > 0,
  page: (d) => Boolean(text(d.title) || text(d.body)),
}

/** Rows that say something. A row with nothing typed in it is not a row. */
export const realItems = (items) =>
  (Array.isArray(items) ? items : []).filter(i => text(i?.title) || text(i?.description))

export const realRows = (rows) =>
  (Array.isArray(rows) ? rows : []).filter(r => text(r?.label) || Number(r?.amount) > 0)

/**
 * What the investment section is worth.
 *
 * A section priced from the list carries a frozen quote — credit, rush, the
 * retainer commitment — and that is the number. A section typed by hand has
 * only its rows, and is their sum. The frozen figure is preferred because the
 * public pages cannot recompute it: they have no access to the policies.
 */
export function investmentTotal(data = {}) {
  if (data.totals) return (Number(data.totals.oneTimeTotal) || 0) + (Number(data.totals.commitment) || 0)
  return realRows(data.rows).reduce((n, r) => n + (parseFloat(r.amount) || 0), 0)
}

/** Rows as lines the engine understands. A hand-typed row is one-time work. */
const asLines = (rows = []) => realRows(rows).map(r => ({
  ...r,
  kind: r.kind || 'addon',
  amount: Number(r.amount) || 0,
  months: r.kind === 'retainer' ? Number(r.months) || null : null,
}))

/**
 * Recompute and freeze the quote on every investment section.
 *
 * Called whenever a line or its price changes. The result is stored on the
 * document — this is the baseline — so a later change to the policies or the
 * price list cannot alter a figure that has already been sent.
 */
export function withTotals(doc, { policies = [] } = {}) {
  if (!doc) return doc
  return {
    ...doc,
    sections: (doc.sections || []).map(s => {
      if (s.type !== 'investment') return s
      const d = s.data || {}
      const rows = realRows(d.rows)
      if (!rows.length) return { ...s, data: { ...d, totals: null } }
      const q = quote(asLines(rows), { policies, compressionPct: Number(d.compression_pct) || 0 })
      return {
        ...s,
        data: {
          ...d,
          totals: {
            servicesSubtotal: q.servicesSubtotal,
            addonsSubtotal: q.addonsSubtotal,
            combinedCredit: q.combinedCredit,
            creditPct: q.creditPct,
            rushFee: q.rushFee,
            rushPct: q.rushPct,
            oneTimeTotal: q.oneTimeTotal,
            monthly: q.monthly,
            commitment: q.commitment,
            schedule: q.schedule,
          },
          warnings: q.warnings,
        },
      }
    }),
  }
}

/** Per-section fill state, written for someone deciding where to spend ten minutes. */
export function sectionState(doc = {}) {
  return (doc.sections || []).map(s => {
    const t = TYPE_BY_ID[s.type] || {}
    const filled = Boolean(hasContent[s.type]?.(s.data || {}, doc))
    const label = s.type === 'page' ? (text(s.data?.title) || 'Untitled page') : (t.label || s.type)
    return { ...s, label, blurb: t.blurb || '', filled, detail: describeSection(s, doc) }
  })
}

function describeSection(s, doc) {
  const d = s.data || {}
  switch (s.type) {
    case 'cover': return text(doc.recipient) || 'No recipient named'
    case 'overview': return text(d.body) ? `${text(d.body).split(/\s+/).length} words` : 'Nothing written'
    case 'scope': {
      const n = realItems(d.items).length
      return n ? `${n} item${n === 1 ? '' : 's'}` : 'No deliverables listed'
    }
    case 'investment': {
      const t = investmentTotal(d)
      return t > 0 ? money(t) : 'No price'
    }
    case 'proof': {
      const st = (d.stats || []).length
      const q = (d.quotes || []).length
      return st || q ? `${st} figure${st === 1 ? '' : 's'} · ${q} quote${q === 1 ? '' : 's'}` : 'Empty — the page is left out'
    }
    case 'about': return text(d.body) ? 'Written' : 'Using the built-in copy'
    case 'terms': return (d.items || []).length ? `${d.items.length} clauses` : 'No terms'
    case 'acceptance': return (d.options || []).length ? `${d.options.length} options` : 'Nothing to accept'
    case 'page': return text(d.body) ? `${text(d.body).split(/\s+/).length} words · from the library` : 'Nothing written'
    default: return ''
  }
}

/**
 * Whether the document can go out, and what stops it.
 *
 * A past date is a blocker rather than a warning: a procurement portal closes,
 * and an upload after it is not late, it is nothing.
 */
export function readiness(doc = {}, now = new Date()) {
  const required = SPINE[doc.kind] ? ['cover', 'overview', 'scope', 'investment'] : []
  const sections = sectionState(doc)
  const missing = sections.filter(s => required.includes(s.type) && !s.filled)
  const blockers = missing.map(s => `${s.label} — ${s.detail.toLowerCase()}`)

  // A {{variable}} nothing can fill would reach the client as written.
  if (doc.kind === 'proposal') {
    const open = unresolvedVars({ title: doc.title, sections: doc.sections || [], team: doc.team || [] }, modelVars(doc))
    if (open.length) blockers.push(`No value for ${open.map(k => `{{${k}}}`).join(', ')}`)
  }

  const days = daysUntil(doc.closes, now)
  if (days !== null && days < 0) {
    blockers.push(`The ${doc.kind === 'bid' ? 'deadline' : 'validity'} was ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`)
  }

  return {
    sections,
    done: required.length - missing.length,
    total: required.length,
    ready: blockers.length === 0,
    blockers,
    warnings: sections.filter(s => !required.includes(s.type) && !s.filled).map(s => s.label),
    days,
  }
}

/**
 * Days until a date, counted in local time.
 *
 * A bare `YYYY-MM-DD` parses as UTC midnight, which in Houston is the evening
 * before — enough to report something closing today as closed yesterday.
 */
export function daysUntil(date, now = new Date()) {
  const [y, m, d] = String(date || '').slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return null
  return Math.round((new Date(y, m - 1, d) - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000)
}

export function describeReadiness(state) {
  if (!state) return ''
  if (state.ready) return 'Ready to send'
  if (state.blockers.length === 1) return state.blockers[0]
  return `${state.done} of ${state.total} sections · ${state.blockers.length} to go`
}

/* ── Drawing it ───────────────────────────────────────────────────────────
 *
 * One renderer. With `editable` every value is marked and every gap is named
 * in the place it will be read; none of that survives into the print, so the
 * thing being edited is the thing that goes out.
 */

const LETTERHEAD = `<div class="letterhead">
  <svg width="42" height="44" viewBox="10 4 42 44" fill="none" aria-hidden="true">
    <polygon points="44.45 34.42 40.45 25.85 30.33 31.75 36.74 17.76 31.31 6.13 23.82 22.22 12.56 46.42 26.51 38.32 33.51 34.26 32.79 46.42 38.42 46.42 50.06 46.42 44.45 34.42"
      stroke="#EF4237" stroke-width="2" stroke-miterlimit="10" fill="none"/>
  </svg>
  <div class="wordmark">Abstract<br><span class="second">Creative</span></div>
  <div class="addr">
    14714 Oakheath River Court<br>
    Cypress, Texas 77429<br>
    832-370-7322 &nbsp;·&nbsp; abstract-creative.com
  </div>
</div>`

const ticks = () => `<div class="ticks">${
  Array.from({ length: 61 }, (_, i) => `<i class="${i % 5 === 0 ? 'tall' : ''}"></i>`).join('')
}</div>`

const paras = (s) => text(s).split(/\n{2,}/).filter(Boolean)
  .map(p => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('\n')

const fmtDate = (d) => {
  const [y, m, day] = String(d || '').slice(0, 10).split('-').map(Number)
  if (!y) return 'Not stated'
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

/**
 * A headline split so the second half carries the accent, the way every cover
 * and section opener in the studio's proposals is set. Split by word, so
 * "Print and Design Services" breaks after "and" rather than inside "Design".
 */
export function accentHeadline(title) {
  const words = text(title).split(/\s+/).filter(Boolean)
  if (words.length < 2) return esc(title)
  const at = Math.ceil(words.length / 2)
  return `${esc(words.slice(0, at).join(' '))}<br><span class="accent">${esc(words.slice(at).join(' '))}</span>`
}

const PAGE_EYEBROWS = {
  service: 'Service',
  case_study: 'Case study',
  portfolio: 'Portfolio',
  about: 'About the studio',
  terms: 'Terms',
}

const SCOPE_PER_PAGE = 6

/**
 * The document's pages.
 *
 * A proposal is drawn in one of the three looks designed in Figma (Editorial,
 * Swiss grid, Bold red; see proposalPages/render.js), with the price list and
 * the studio's settings filling what the sections do not carry. A bid keeps
 * the warm-ground response layout below, which procurement readers know.
 */
export function renderPages(doc = {}, { editable = false, look, settings, catalog } = {}) {
  if (doc.kind === 'proposal') {
    return lookPages(doc, { look: look || doc.look, settings, catalog, editable })
  }
  const ed = (path) => (editable ? ` data-edit="${path}"` : '')

  const val = (v, path, placeholder, fallback = '—') => {
    const t = text(v)
    if (t) return `<span${ed(path)}>${esc(t)}</span>`
    return editable ? `<span${ed(path)} class="token">${esc(placeholder)}</span>` : esc(fallback)
  }

  const rich = (v, path, placeholder) => {
    const t = text(v)
    if (t) return `<div${ed(path)}>${paras(t)}</div>`
    return editable ? `<div${ed(path)}><p class="token">${esc(placeholder)}</p></div>` : ''
  }

  const pages = []
  let page = 1
  const foot = (label) => `<div class="foot"><span>${esc(label)}</span><span class="n">${String(++page).padStart(2, '0')}</span></div>`
  const open = (eyebrow, headline) => `<div class="runhead"></div>
  <div class="eyebrow">${esc(eyebrow)}</div>
  <h1>${headline}</h1>`

  for (const [index, s] of (doc.sections || []).entries()) {
    const d = s.data || {}
    const at = `sections.${index}`
    const has = hasContent[s.type]?.(d, doc)
    if (!has && !editable && s.type !== 'cover') continue

    if (s.type === 'cover') {
      pages.push(`<div class="page cover">
  <div class="bar"></div>
  ${LETTERHEAD}
  <div class="org">${esc(doc.eyebrow || 'Proposal')}</div>
  <h1${ed('title')}>${accentHeadline(doc.title || 'Untitled')}</h1>
  <div class="deck">${esc(doc.deckPrefix || 'Prepared for')} ${val(doc.recipient, 'recipient', '[Recipient]', 'you')}${
    text(doc.reference) || editable ? `, ${esc((doc.referenceLabel || 'reference').toLowerCase())} ${val(doc.reference, 'reference', '[Number]', '')}` : ''
  }.</div>
  ${ticks()}
  <div class="meta">
    <div><div class="k">Prepared for</div><div class="v">${val(doc.contact, 'contact', '[Contact]')}<br>${val(doc.recipient, 'recipient.2', '[Recipient]')}</div></div>
    <div><div class="k">Prepared by</div><div class="v">Efren Cavazos, Brand Strategist<br>Abstract Creative</div></div>
    <div><div class="k">${esc(doc.referenceLabel || 'Reference')}</div><div class="v">${val(doc.reference, 'reference', '[Number]')}</div></div>
    <div><div class="k">${esc(doc.closesLabel || 'Closes')}</div><div class="v">${esc(fmtDate(doc.closes))}</div></div>
  </div>
  <div class="fine">${esc(doc.confidentiality || 'This document and its contents are submitted solely for evaluation by the recipient named above. All concepts, frameworks and deliverables described remain the intellectual property of Abstract Creative.')}</div>
</div>`)
      continue
    }

    if (s.type === 'overview') {
      pages.push(`<div class="page">
  ${open(doc.kind === 'bid' ? 'The opportunity' : 'The opportunity', 'Why this<br><span class="accent">response</span>')}
  ${rich(d.body, `${at}.data.body`, 'Open by naming what they asked for, then what we do about it.')}
  ${foot('The opportunity')}
</div>`)
      continue
    }

    if (s.type === 'scope') {
      const items = realItems(d.items)
      // A service from the price list carries its deliverables and prints as a
      // phase block — numeral, name, schedule and price down the rail, the work
      // packages beside it — the way the studio's own proposals are set. A
      // hand-typed item has only a title and a description and stays a row.
      // Phase blocks are tall, so they take two to a page; rows take six.
      const weight = (it) => ((it.deliverables || []).length ? 3 : 1)
      const groups = []
      let current = []
      let load = 0
      for (const it of items) {
        if (current.length && load + weight(it) > SCOPE_PER_PAGE) { groups.push(current); current = []; load = 0 }
        current.push(it)
        load += weight(it)
      }
      if (current.length || (!items.length && editable)) groups.push(current)

      let offset = 0
      groups.forEach((group, gi) => {
        const last = gi === groups.length - 1
        const start = offset
        offset += group.length
        pages.push(`<div class="page">
  ${gi === 0
    ? open('Scope of work', 'What is<br><span class="accent">delivered</span>')
    : '<div class="runhead"></div><div class="eyebrow">Scope of work, continued</div>'}
  ${group.map((it, i) => {
    const at2 = `${at}.data.items.${start + i}`
    const numeral = String(start + i + 1).padStart(2, '0')
    const drop = editable ? `<span data-drop="${at2}" title="Remove">×</span>` : ''
    if ((it.deliverables || []).length) {
      return `<div class="phase" style="position:relative">
    <div class="rail">
      <div class="num">${numeral}</div>
      <div class="name">${val(it.title, `${at2}.title`, '[Service]', '')}</div>
      ${it.schedule ? `<div class="when">${esc(it.schedule)}</div>` : ''}
      ${Number(it.amount) > 0 ? `<div class="cost">${money(it.amount)}${it.kind === 'retainer' ? '/mo' : ''}</div>` : ''}
    </div>
    <div class="body">
      ${text(it.description) ? `<p>${val(it.description, `${at2}.description`, '', '')}</p>` : ''}
      <ul class="items">
        ${it.deliverables.map((w, wi) => `<li>${val(w, `${at2}.deliverables.${wi}`, '[Deliverable]', '')}</li>`).join('\n        ')}
      </ul>
    </div>
    ${drop}
  </div>`
    }
    return `<div class="defs"><div class="def" style="position:relative">
    <div class="k"><span class="accent">${numeral}</span><br>${val(it.title, `${at2}.title`, '[Deliverable]', '')}</div>
    <div class="r"></div>
    <div class="v">${val(it.description, `${at2}.description`, '[What it includes]', '')}</div>
    ${drop}
  </div></div>`
  }).join('\n  ')}
  ${editable && last ? `<span data-add="${at}.data.items">+ Deliverable</span>` : ''}
  ${foot('Scope of work')}
</div>`)
      })
      continue
    }

    if (s.type === 'investment') {
      const rows = realRows(d.rows)
      const t = d.totals
      const priceCell = (r, i) => {
        if (!(Number(r.amount) > 0)) {
          return editable ? `<span${ed(`${at}.data.rows.${i}.amount`)} data-raw="" class="token">[Amount]</span>` : '—'
        }
        const amount = `<span${ed(`${at}.data.rows.${i}.amount`)} data-raw="${Number(r.amount)}">${money(r.amount)}</span>`
        return r.kind === 'retainer' ? `${amount}/mo${r.months ? `<span class="sub">${r.months}-month minimum</span>` : ''}` : amount
      }
      pages.push(`<div class="page">
  ${open('Investment', doc.kind === 'bid' ? 'What it<br><span class="accent">costs</span>' : 'Ways to<br><span class="accent">proceed</span>')}
  <table>
    <thead><tr><th>Line</th><th class="num">Amount</th></tr></thead>
    <tbody>
      ${rows.map((r, i) => `<tr>
        <td>${val(r.label, `${at}.data.rows.${i}.label`, '[Line]', '')}${r.sub ? `<span class="sub">${esc(r.sub)}</span>` : ''}</td>
        <td class="num">${priceCell(r, i)}</td>
      </tr>`).join('\n      ')}
      ${t && t.combinedCredit > 0 ? `<tr><td>Combined engagement credit<span class="sub">${t.creditPct}% on three or more services booked together</span></td><td class="num">− ${money(t.combinedCredit)}</td></tr>` : ''}
      ${t && t.rushFee > 0 ? `<tr><td>Rush delivery<span class="sub">${t.rushPct}% on a compressed timeline</span></td><td class="num">${money(t.rushFee)}</td></tr>` : ''}
      ${t
        ? `<tr class="total"><td>One-time total</td><td class="num">${money(t.oneTimeTotal)}</td></tr>
      ${t.monthly > 0 ? `<tr class="total"><td>Ongoing<span class="sub">${money(t.commitment)} over the minimum term</span></td><td class="num">${money(t.monthly)}/mo</td></tr>` : ''}`
        : `<tr class="total"><td>${esc(d.totalLabel || 'Total')}</td><td class="num">${money(investmentTotal(d))}</td></tr>`}
    </tbody>
  </table>
  ${editable ? `<span data-add="${at}.data.rows">+ Line</span>` : ''}
  ${t && (t.schedule || []).length ? `<h3>Payment schedule</h3>
  <table class="compact">
    <tbody>
      ${t.schedule.map(p => `<tr><td>${esc(p.label)} · ${p.pct}%</td><td class="num">${money(p.amount)}</td></tr>`).join('\n      ')}
    </tbody>
  </table>
  ${t.monthly > 0 ? '<p class="small">Ongoing services are billed monthly in advance.</p>' : ''}` : ''}
  ${text(d.note) || editable ? `<div class="note"><h3>What the figures cover</h3>${rich(d.note, `${at}.data.note`, 'What is included, what is invoiced when, and what is not covered.')}</div>` : ''}
  ${foot('Investment')}
</div>`)
      continue
    }

    if (s.type === 'proof') {
      pages.push(`<div class="page">
  ${open('Proof', 'Results that<br><span class="accent">followed</span>')}
  <div class="defs">
    ${(d.stats || []).map((st, i) => `<div class="def">
      <div class="k"><span class="accent">${val(st.figure, `${at}.data.stats.${i}.figure`, '[Figure]', '')}</span></div>
      <div class="r"></div>
      <div class="v">${val(st.caption, `${at}.data.stats.${i}.caption`, '[What it measured]', '')}${st.client ? `<span class="sub">${esc(st.client)}</span>` : ''}</div>
    </div>`).join('\n    ')}
  </div>
  ${(d.quotes || []).map((q, i) => `<div class="pull">
    <p>${val(q.quote, `${at}.data.quotes.${i}.quote`, '[What they said]', '')}</p>
    <div class="small">${esc([q.name, q.company].filter(Boolean).join(' · '))}</div>
  </div>`).join('\n  ')}
  ${foot('Proof')}
</div>`)
      continue
    }

    if (s.type === 'about') {
      pages.push(`<div class="page">
  ${open('About the studio', 'Abstract<br><span class="accent">Creative</span>')}
  <div class="cols">${rich(d.body, `${at}.data.body`, 'Who the studio is. The same paragraph on every document.')}</div>
  ${foot('About the studio')}
</div>`)
      continue
    }

    if (s.type === 'terms') {
      pages.push(`<div class="page">
  ${open('Service agreement', 'Terms of<br><span class="accent">engagement</span>')}
  <div class="defs">
    ${(d.items || []).map((t, i) => `<div class="def">
      <div class="k"><span class="accent">${String(i + 1).padStart(2, '0')}</span><br>${val(t.title, `${at}.data.items.${i}.title`, '[Clause]', '')}</div>
      <div class="r"></div>
      <div class="v">${val(t.body, `${at}.data.items.${i}.body`, '[What it says]', '')}</div>
    </div>`).join('\n    ')}
  </div>
  ${foot('Service agreement')}
</div>`)
      continue
    }

    if (s.type === 'page') {
      const eyebrow = PAGE_EYEBROWS[d.kind] || 'From the studio'
      pages.push(`<div class="page">
  <div class="runhead"></div>
  <div class="eyebrow">${esc(eyebrow)}</div>
  ${text(d.title)
    ? `<h1${ed(`${at}.data.title`)}>${accentHeadline(d.title)}</h1>`
    : (editable ? `<h1${ed(`${at}.data.title`)} class="token">[Page title]</h1>` : '')}
  ${rich(d.body, `${at}.data.body`, 'What this page says.')}
  ${foot(text(d.title) || eyebrow)}
</div>`)
      continue
    }

    if (s.type === 'acceptance') {
      pages.push(`<div class="page">
  ${open('Acceptance', 'Select and<br><span class="accent">we begin</span>')}
  ${rich(d.body, `${at}.data.body`, 'What signing here means, and what happens next.')}
  <table>
    <tbody>
      ${(d.options || []).map((o, i) => `<tr>
        <td>☐ &nbsp; <strong>${val(o.label, `${at}.data.options.${i}.label`, '[Option]', '')}</strong>${o.detail ? `<span class="sub">${esc(o.detail)}</span>` : ''}</td>
        <td class="num">${o.amount ? money(o.amount) : esc(o.note || '')}</td>
      </tr>`).join('\n      ')}
    </tbody>
  </table>
  <div class="sigs">
    <div class="sig"><div class="line"></div><div class="k">Client signature</div><div class="v">${esc(doc.contact || '')}${doc.recipient ? ` — ${esc(doc.recipient)}` : ''}</div></div>
    <div class="sig"><div class="line"></div><div class="k">For Abstract Creative</div><div class="v">Efren Cavazos — Brand Strategist</div></div>
  </div>
  ${foot('Acceptance')}
</div>`)
      continue
    }
  }

  return pages.join('\n')
}

/* ── Editing ──────────────────────────────────────────────────────────────
 *
 * The canvas hands back a dotted path and a string. Everything it can address
 * is inside `sections`, or one of the header fields; nothing else is writable
 * from the page, so a stray `data-edit` cannot reach the record.
 */

const HEADER_FIELDS = ['title', 'recipient', 'contact', 'reference', 'deck']

/** A copy of the document with one value changed. Returns null if the path is not writable. */
export function setAtPath(doc, path, value) {
  const parts = String(path || '').split('.')
  const head = parts[0]

  if (HEADER_FIELDS.includes(head)) return { ...doc, [head]: value || null }
  if (head !== 'sections') return null

  const index = Number(parts[1])
  const sections = [...(doc.sections || [])]
  if (!sections[index]) return null

  // sections.<i>.data.<...>
  if (parts[2] !== 'data') return null
  const rest = parts.slice(3)
  if (!rest.length) return null

  const next = structuredCloneish(sections[index])
  let cursor = next.data
  for (let i = 0; i < rest.length - 1; i += 1) {
    const key = rest[i]
    const k = /^\d+$/.test(key) ? Number(key) : key
    if (cursor[k] === undefined || cursor[k] === null) return null
    cursor = cursor[k]
  }
  const leaf = rest[rest.length - 1]
  cursor[/^\d+$/.test(leaf) ? Number(leaf) : leaf] = value
  sections[index] = next
  return { ...doc, sections }
}

/** A row added to or removed from a list inside a section. */
export function addAtPath(doc, path, row) {
  const parts = String(path || '').split('.')
  if (parts[0] !== 'sections' || parts[2] !== 'data') return null
  const index = Number(parts[1])
  const sections = [...(doc.sections || [])]
  if (!sections[index]) return null
  const key = parts[3]
  const next = structuredCloneish(sections[index])
  next.data[key] = [...(next.data[key] || []), row]
  sections[index] = next
  return { ...doc, sections }
}

export function removeAtPath(doc, path) {
  const parts = String(path || '').split('.')
  if (parts[0] !== 'sections' || parts[2] !== 'data') return null
  const index = Number(parts[1])
  const at = Number(parts[4])
  const sections = [...(doc.sections || [])]
  if (!sections[index]) return null
  const key = parts[3]
  const next = structuredCloneish(sections[index])
  next.data[key] = (next.data[key] || []).filter((_, i) => i !== at)
  sections[index] = next
  return { ...doc, sections }
}

// The document is plain JSON; this keeps the file working where
// `structuredClone` is not defined (older Safari, some test environments).
const structuredCloneish = (v) => JSON.parse(JSON.stringify(v))
